import React, { useLayoutEffect, useRef, useState, useEffect, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { IMAGE_VERSION } from '../../../version.js';

gsap.registerPlugin(ScrollTrigger);

const brandsBase = [
  { id: "canon", name: "Canon", src: `/assets/brands/canon.png?ver=${IMAGE_VERSION}` },
  { id: "logitech", name: "Logitech", src: `/assets/brands/logitech.png?ver=${IMAGE_VERSION}` },
  { id: "genius", name: "Genius", src: `/assets/brands/genius.png?ver=${IMAGE_VERSION}` },
  { id: "remax", name: "Remax", src: `/assets/brands/remax.png?ver=${IMAGE_VERSION}` },
  { id: "deerma", name: "Deerma", src: `/assets/brands/deerma.png?ver=${IMAGE_VERSION}` },
  { id: "xiaomi", name: "Xiaomi", src: `/assets/brands/xiaomi.png?ver=${IMAGE_VERSION}` },
  { id: "dreame", name: "Dreame", src: `/assets/brands/dreame.png?ver=${IMAGE_VERSION}` },
  { id: "doogee", name: "Doogee", src: `/assets/brands/doogee.png?ver=${IMAGE_VERSION}` },
];

const BrandsSection = () => {
  const containerRef = useRef(null);
  const rowsRef = useRef([]);
  const [theme, setTheme] = useState(typeof themeManager !== 'undefined' ? themeManager.getCurrentTheme() : 'light');

  useEffect(() => {
    const handleThemeChange = (event) => setTheme(event.detail.theme);
    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  const rowPacks = useMemo(() => {
    const createRow = (offset) => {
      const shifted = [...brandsBase.slice(offset), ...brandsBase.slice(0, offset)];
      // Povećavamo na 4 kopije za mobilni da bi skrol bio tečniji bez "rupa"
      return [...shifted, ...shifted, ...shifted, ...shifted];
    };
    return [createRow(0), createRow(3), createRow(6)];
  }, []);

  useLayoutEffect(() => {
    let ctx = gsap.context(() => {
      const isMobile = window.innerWidth < 768;

      rowsRef.current.forEach((row, index) => {
        const isMiddleRow = index === 1;
        
        // Dinamički put: na mobilnom povećavamo distancu da bi logoi "brže" prolazili
        const moveAmount = isMobile ? 55 : 30; 
        const startX = isMiddleRow ? -moveAmount : 0;
        const endX = isMiddleRow ? 0 : -moveAmount;

        gsap.fromTo(row, 
          { xPercent: startX },
          {
            xPercent: endX,
            ease: "none",
            force3D: true, // Forsira GPU
            scrollTrigger: {
              trigger: containerRef.current,
              start: "top bottom",
              end: "bottom top",
              scrub: 1, 
              invalidateOnRefresh: true,
            }
          }
        );
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={containerRef} 
      className={`relative w-full overflow-hidden py-10 md:py-24 transition-colors duration-500 ${
        theme === 'dark' ? 'bg-[#0F1115]' : 'bg-[#F3F4F6]'
      }`}
      style={{ contain: 'layout style' }}
    >
      {/* Gradients */}
      <div className={`absolute left-0 top-0 bottom-0 w-20 md:w-80 z-20 pointer-events-none bg-gradient-to-r ${
        theme === 'dark' ? 'from-[#0F1115]' : 'from-[#F3F4F6]'
      } to-transparent`} />
      <div className={`absolute right-0 top-0 bottom-0 w-20 md:w-80 z-20 pointer-events-none bg-gradient-to-l ${
        theme === 'dark' ? 'from-[#0F1115]' : 'from-[#F3F4F6]'
      } to-transparent`} />

      <div className="relative z-10 flex flex-col gap-6 md:gap-16 rotate-[-1.5deg] scale-105 md:scale-110">
        {rowPacks.map((rowBrands, rowIndex) => (
          <div 
            key={rowIndex}
            ref={(el) => (rowsRef.current[rowIndex] = el)}
            className="flex items-center whitespace-nowrap will-change-transform"
          >
            {rowBrands.map((brand, i) => (
              <div 
                key={`${rowIndex}-${i}`} 
                className="flex-shrink-0 flex items-center justify-center px-6 md:px-20"
              >
                {/* Dimenzije: w-24 (mobile) vs w-56 (PC) */}
                <div className="w-24 h-12 md:w-56 md:h-24 flex items-center justify-center group cursor-pointer">
                  <img 
                    src={brand.src}
                    alt={`PC Centar partner: ${brand.name}`}
                    className={`max-w-full max-h-full object-contain transition-[filter,opacity,transform] duration-500 group-hover:scale-110 ${
                      theme === 'dark' 
                        ? 'grayscale brightness-200 opacity-60 group-hover:grayscale-0 group-hover:brightness-100 group-hover:opacity-100' 
                        : 'grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100'
                    }`}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

export default BrandsSection;